import { Dashboard } from '@app/layouts/dashboard-layout/dashboard'
import { MobileSettings } from '@app/layouts/mobile-settings/mobile-settings'
import { VerifyPage } from '@pages/auth/verify-page'
import { Appointment } from '@pages/student/appointment/appointment'
import { History } from '@pages/student/history/history'
import { DashboardHome } from '@pages/student/home/home'
import { Information } from '@pages/student/information/information'
import { Messenger } from '@pages/student/messenger/messenger'
import { PostPaymentPageFail } from '@pages/student/post-payment-page-fail/post-payment-page-fail'
import { PostPaymentPage } from '@pages/student/post-payment-page/post-payment-page'
import { Settings } from '@pages/student/settings/settings'
import { Teacher } from '@pages/student/teacher/teacher'
import { TeacherPacksPage } from '@pages/student/teacher-packs/teacher-packs'
import { Teachers } from '@pages/student/teachers/teachers'
import { useProfile } from '@shared/api/useProfile'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import { RedirectsFromDashboard } from './redirects-from-dashboard'
import { Onboarding } from '@pages/student/onboarding/onboarding'
import { VideoSettings } from '@pages/settings/video-settings/video-settings'
import { Notifications } from '@pages/settings/notification-settings/notification'
import { UpdatePassword } from '@pages/settings/update-password/update-password'
import { AppearanceSettings } from '@pages/settings/appearance-settings'
import { ChildrenSettings } from '@pages/settings/children-settings'
import { ProfileSettings } from '@pages/settings/child-profile-settings/profile-settings'
import { useOnboardNeeded } from '@shared/api/useOnboardNeeded'
import { Loader } from '@shared/ui/loader/loader'
import { UnsubscribePage } from '@pages/auth/unsubscribe-page'
import { TeacherProfile } from '@pages/student/teacher-profile/teacher-profile'
import { useProfiles } from '@entities/profile/api/useProfiles'
import { useActiveProfileInfo } from '@entities/profile/lib/use-active-profile-info'
import { hasStoredProfileSelection } from '@utils/lib/activeProfile'
import { ProfileSelectPage } from '@pages/student/profile-select/profile-select'


// Разделы, недоступные при активном детском профиле (постановка «Родители и дети»):
// чат, бронирование, покупка пакетов и оплата — только для владельца аккаунта.
const CHILD_MODE_FORBIDDEN_PATHS = ['/messenger', '/teachers', '/teacher/booking', '/teacher/packs', '/post-payment', '/settings/children', '/settings/notifications', '/settings/security']

const ProfileGuard = () => {
	const { data: profiles = [], isFetched } = useProfiles()
	const { activeProfileId, isChildMode } = useActiveProfileInfo()
	const { pathname } = useLocation()

	if (!isFetched) {
		return <Loader />
	}

	// Несколько профилей и ни один не выбран — отправляем на экран выбора.
	// Маркер владельца (явный выбор родительского контекста) считается выбором.
	if (profiles.length > 1 && !activeProfileId && !hasStoredProfileSelection()) {
		return <Navigate to="/profile-select" replace />
	}

	// Прямые переходы по недоступным детскому профилю разделам
	if (isChildMode && CHILD_MODE_FORBIDDEN_PATHS.some((path) => pathname.startsWith(path))) {
		return <Navigate to="/" replace />
	}

	return <Dashboard />
}

export const StudentRouterGroup = () => {
	const {data: profile, isFetched} = useProfile()
	const {isFetched: onboardingNeededIsFetched} = useOnboardNeeded({
		enabled: !!profile && !profile?.onboarding_passed
	})

	if(!isFetched || !onboardingNeededIsFetched){
		return <Loader/>
	}

	const isFull = () => {
		// return profile?.onboarding_passed || onboard_needed?.onboarding_needed === false
		return profile?.onboarding_passed
	}

	return (
		(process.env?.REACT_APP_ONBOARDING_STATIC_VISIBLE === 'visible' ? false : isFull()) ?
		<Routes>
			<Route element={<Outlet/>}>
				<Route path="/verify-email/:token" element={<VerifyPage />} />
				<Route path="/unsubscribe/:token" element={<UnsubscribePage />} />
				<Route path="/appointment/:appointmentId" element={<Appointment/>} />
			</Route>
			<Route path="/profile-select" element={<ProfileSelectPage/>} />
			<Route element={<ProfileGuard />}>
				<Route
					path="/"
					element={
						<DashboardHome/> 
					}
				/>
				<Route path="/teachers" element={<Teachers />} />
				{profile?.access_level !== 'restricted' ? <>
					<Route path='/teacher/booking/:id' element={<Teacher />}/>
					<Route path='/teacher/packs/:id' element={<TeacherPacksPage />}/>
					<Route path="/teacher/:id" element={<TeacherProfile />} />
					<Route path="/post-payment/:appointmentId" element={<PostPaymentPage/>} />
					<Route path="/post-payment-fail/:appointmentId" element={<PostPaymentPageFail/>} />
				</> : <></>}
				<Route path="/history" element={<History />} />
				<Route path="/settings" element={<Settings />} />
				<Route element={<Outlet/>}>
					<Route path="/verify-email/:token" element={<VerifyPage />} />
				</Route>
				<Route element={<MobileSettings />}>
					<Route path="/settings/profile" element={<ProfileSettings />} />
					<Route path="/settings/video-and-audio" element={<VideoSettings />} />
					<Route path="/settings/notifications" element={<Notifications />} />
					<Route path="/settings/security" element={<UpdatePassword />} />
					<Route path="/settings/appearance" element={<AppearanceSettings />} />
					<Route path="/settings/children" element={<ChildrenSettings />} />
				</Route>
				<Route path="/messenger" element={<Messenger/>} /> 
				<Route path="/info" element={<Information/>} />
				
			</Route>
			<Route path="*" element={<RedirectsFromDashboard/>} />
		</Routes>
		:
		<Routes>
				<Route path="/" element={<Onboarding/>} />
				<Route path="*" element={<Navigate to={'/'}/>} />
		</Routes>
	)
}
